import { Tag } from 'app/shared/collections/tag';
import {
    alwaysNull,
    AutoResolvingAPIBackedFormField,
    hasValue,
    isTripleEqual,
    passThrough,
} from 'app/shared/form/AutoResolvingAPIBackedFormField';
import Constant from 'app/shared/utilities/constant';
import { isNil, sortBy, differenceWith, isEqual, isEmpty } from 'lodash';

function nilToEmptyString(x: null | undefined | string): string {
    return isNil(x) ? '' : x;
}

export function emptyStringToNull(x: null | undefined | string): string {
    return isNil(x) || x.length === 0 ? null : x;
}

function hasStringLength(x: null | undefined | string): boolean {
    return isNil(x) ? false : x.length > 0;
}

function sortTagArray(x: null | undefined | Array<Tag>): Array<Tag> {
    return isNil(x) ? [] : sortBy(x, 'name');
}

function isTagsEqual(a: Array<Tag>, b: Array<Tag>): boolean {
    return a.map((t) => t.id).join('-') === b.map((t) => t.id).join('-');
}

export function isArrayEqual(x: Array<any>, y: Array<any>) {
    const xEmpty = isEmpty(x);
    const yEmpty = isEmpty(y);

    // if neither array is empty, compare them
    if (!xEmpty && !yEmpty) {
        return isEmpty(differenceWith(x, y, isEqual));
    }

    // otherwise, only return true if they are both empty
    return xEmpty && yEmpty;
}

export function createTagsFormField(
    initialTags: Array<Tag>,
    displayName?: string,
): AutoResolvingAPIBackedFormField<Array<Tag>> {
    return new AutoResolvingAPIBackedFormField<Array<Tag>>(
        initialTags,
        displayName ?? 'Tags',
        alwaysNull,
        isTagsEqual,
        sortTagArray,
    );
}

export function createRequiredStringFormField(
    initialValue: string,
    name: string,
): AutoResolvingAPIBackedFormField<string> {
    return new AutoResolvingAPIBackedFormField<string>(
        initialValue,
        name,
        (v) => (hasStringLength(v) ? null : `${name} is required`),
        isTripleEqual,
        nilToEmptyString,
    );
}

export function createNonRequiredStringFormField(
    initialValue: string,
    name: string,
): AutoResolvingAPIBackedFormField<string> {
    return new AutoResolvingAPIBackedFormField<string>(
        initialValue,
        name,
        alwaysNull,
        isTripleEqual,
        nilToEmptyString,
    );
}

export function hasValidHostName(hostname: string) {
    if (hasStringLength(hostname)) {
        if (Constant.IPv4Regex.test(hostname)) {
            return 'IPv4 address detected, please enter a hostname';
        } else if (Constant.HostnameRegex.test(hostname)) {
            return null;
        }
    }
    return null;
}

export function hasValidIPAddress(address: string) {
    if (hasStringLength(address)) {
        if (Constant.IPv4Regex.test(address)) {
            return null;
        } else {
            return 'Invalid IPv4 address';
        }
    }
    return null;
}

export function createRequiredNumberFormField(
    initialValue: number,
    name: string,
): AutoResolvingAPIBackedFormField<number> {
    return new AutoResolvingAPIBackedFormField<number>(initialValue, name, (v) =>
        hasValue(v) ? null : `${name} is required`,
    );
}

function isHeightValid(x: number): string {
    return x >= 0 || isNil(x) ? null : 'Height has to be a positive number';
}

export function createNonRequiredNumberFormField(
    initialValue: number,
    name: string,
): AutoResolvingAPIBackedFormField<number> {
    return new AutoResolvingAPIBackedFormField<number>(
        initialValue,
        name,
        alwaysNull,
        isTripleEqual,
        passThrough,
    );
}

export function createHeightNumberFormField(
    initialValue: number,
    name: string,
): AutoResolvingAPIBackedFormField<number> {
    return new AutoResolvingAPIBackedFormField<number>(
        initialValue,
        name,
        isHeightValid,
        isTripleEqual,
        passThrough,
    );
}

export function createBooleanFormField(
    initialValue: boolean,
    name: string,
): AutoResolvingAPIBackedFormField<boolean> {
    return new AutoResolvingAPIBackedFormField<boolean>(
        initialValue,
        name,
        alwaysNull,
        isTripleEqual,
        passThrough,
    );
}

// This creates an empty object that explodes on a particular key change
// This is useful if you're looking to remove a key from an object but not sure how far it's reach actually is
